import { ProgressResponse, RecommendationsActivityType, UserPoints } from 'src/api/me/types';
import { defineMessages, useIntl } from 'react-intl';
import LearningStatistic from 'src/components/LearningStatistic';
import { useCallback, useMemo } from 'react';
import { Activities } from 'src/components/MiniPlayer';
import * as S from './styles';
import { mapResponseToProps } from './utils';

export interface MyScoreProps {
  collapsible?: boolean;
  selectedKey?: RecommendationsActivityType;
  progress: ProgressResponse;
  userPoints: UserPoints;
  onClick?: (key: RecommendationsActivityType) => void;
  supportedActivities: Activities[];
}

const msgs = defineMessages({
  title: { id: 'homeScreen_myScore_title' },
  points: { id: 'homeScreen_myScore_points_abbreviature_web' },
  contentMediaPoints: { id: 'homeScreen_hearMyWords' },
  learnablePoints: { id: 'homeScreen_learnWords' },
  missionPoints: { id: 'homeScreen_useMyWords' },
  learnablesFullyGrownCount: { id: 'homeScreen_learnWords_wordsFullyLearned' },
  learnablesTouchedButNotFullyGrownCount: { id: 'homeScreen_learnWords_wordsStartedLearning' },
  contentMediaWatched: { id: 'homeScreen_hearMyWords_newVideosWatched' },
  contentMediaReplayed: { id: 'homeScreen_hearMyWords_videosReplayed' },
  missionsCompleted: { id: 'homeScreen_useMyWords_newConversationsCompleted' },
  missionsReplayed: { id: 'homeScreen_useMyWords_conversationsReplayed' },
});

const MyScore = ({
  selectedKey,
  collapsible,
  progress,
  userPoints,
  onClick,
  supportedActivities,
}: MyScoreProps) => {
  const intl = useIntl();
  const scoreList = useMemo(
    () =>
      mapResponseToProps(progress, userPoints, supportedActivities).map(item => ({
        ...item,
        title: intl.formatMessage(msgs[item.title as keyof typeof msgs]),
        items: item.items.map(i => ({
          value: i.value,
          description: intl.formatMessage(msgs[i.description as keyof typeof msgs]),
        })),
      })),
    [progress, userPoints, supportedActivities, intl],
  );
  const toggleCollapsed = useCallback(
    (key: RecommendationsActivityType) => () => {
      /* istanbul ignore else: empty else branch */
      if (collapsible) {
        return onClick?.(key);
      }
    },
    [collapsible, onClick],
  );

  return (
    <S.Wrapper>
      <S.Head>
        <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
        <S.Points data-testid="my_progress_total_score_label">
          {intl.formatMessage(msgs.points, { points: userPoints.totalPoints })}
        </S.Points>
      </S.Head>
      <S.Body>
        {scoreList.map(item => (
          <LearningStatistic
            {...item}
            key={item.key}
            activityKey={item.key}
            selected={selectedKey === item.key}
            onClick={toggleCollapsed(item.key)}
          />
        ))}
      </S.Body>
    </S.Wrapper>
  );
};

export default MyScore;
